import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { branchesApi, adminApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState, StatusBadge } from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

export function BranchesPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [q, setQ] = useState('');

  const { data, isLoading, error } = useQuery({ queryKey: ['branches'], queryFn: branchesApi.list });

  const createMut = useMutation({
    mutationFn: (p: any) => branchesApi.create(p),
    onSuccess: () => { toast.success('Branch created'); qc.invalidateQueries({ queryKey: ['branches'] }); setShowCreate(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }: any) => branchesApi.update(id, payload),
    onSuccess: () => { toast.success('Branch updated'); qc.invalidateQueries({ queryKey: ['branches'] }); setEditing(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, status }: any) => branchesApi.update(id, { status }),
    onSuccess: (_d, v) => { toast.success(v.status === 'active' ? 'Branch activated' : 'Branch deactivated'); qc.invalidateQueries({ queryKey: ['branches'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const { kpis, filteredRows } = useMemo(() => {
    const rows = (data ?? []) as any[];
    const k = { total: rows.length, active: rows.filter((r) => r.status === 'active').length, inactive: rows.filter((r) => r.status !== 'active').length };
    const lower = q.trim().toLowerCase();
    const filtered = lower ? rows.filter((r) => [r.code, r.name, r.email, r.phone].filter(Boolean).join(' ').toLowerCase().includes(lower)) : rows;
    return { kpis: k, filteredRows: filtered };
  }, [data, q]);

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader eyebrow="Administration" title="Branches"
        subtitle="Physical locations — stores, warehouses, and offices your tenant operates."
        actions={<button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New branch</button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <KpiTile label="Total branches" value={kpis.total} />
        <KpiTile label="Active" value={kpis.active} accent="sage" />
        <KpiTile label="Inactive" value={kpis.inactive} />
      </div>

      <div className="card p-1.5 mb-4 flex gap-2 items-center">
        <div className="flex-1 flex items-center gap-2 px-3">
          <SearchIcon />
          <input type="text" placeholder="Search by code, name, email…" value={q} onChange={(e) => setQ(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-ink-400 py-2 min-w-0" />
        </div>
      </div>

      {isLoading ? <div className="card"><LoadingState rows={4} /></div>
        : error ? <ErrorState error={error} />
        : !filteredRows.length ? (
          <div className="card">
            <EmptyState message={q ? 'No branches match' : 'No branches yet'}
              hint={q ? 'Try a different keyword.' : 'Add your first location to get started.'}
              action={!q && <button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New branch</button>}
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr><th>Branch</th><th>Contact</th><th>Tax reg.</th><th>Status</th><th className="text-right w-40">Actions</th></tr>
                </thead>
                <tbody>
                  {filteredRows.map((b: any) => (
                    <tr key={b.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <BranchAvatar name={b.name} />
                          <div className="min-w-0">
                            <div className="font-semibold text-ink-900">{b.name}</div>
                            <div className="font-mono text-xs text-ink-500">{b.code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-sm text-ink-600">
                        {b.email && <div className="truncate">{b.email}</div>}
                        {b.phone && <div className="text-xs text-ink-500">{b.phone}</div>}
                        {!b.email && !b.phone && <span className="text-ink-400">—</span>}
                      </td>
                      <td className="font-mono text-xs text-ink-600">{b.tax_registration || <span className="text-ink-400">—</span>}</td>
                      <td><StatusBadge status={b.status} /></td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button className="text-xs font-medium px-2 py-1 rounded hover:bg-canvas-subtle transition-colors text-ink-600"
                            onClick={() => setEditing(b)}>Edit</button>
                          <button
                            className={`text-xs font-medium px-2 py-1 rounded transition-colors ${b.status === 'active' ? 'text-ink-500 hover:bg-canvas-subtle' : 'text-sage-700 hover:bg-sage-50'}`}
                            disabled={toggleMut.isPending}
                            onClick={() => toggleMut.mutate({ id: b.id, status: b.status === 'active' ? 'inactive' : 'active' })}>
                            {b.status === 'active' ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {showCreate && <BranchModal onSubmit={(p) => createMut.mutate(p)} onClose={() => setShowCreate(false)} loading={createMut.isPending} />}
      {editing && <BranchModal initial={editing} onSubmit={(p) => updateMut.mutate({ id: editing.id, payload: p })} onClose={() => setEditing(null)} loading={updateMut.isPending} />}
    </div>
  );
}

function BranchAvatar({ name }: { name: string }) {
  const hash = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
  const tones = ['bg-sage-100 text-sage-700','bg-blue-100 text-blue-700','bg-amber-100 text-amber-800','bg-purple-100 text-purple-700','bg-teal-100 text-teal-700'];
  return (
    <div className={clsx('w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0', tones[hash % tones.length])}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/>
      </svg>
    </div>
  );
}

function BranchModal({ initial, onSubmit, onClose, loading }: {
  initial?: any; onSubmit: (p: any) => void; onClose: () => void; loading: boolean;
}) {
  const isEdit = !!initial;
  const reservationId = useRef<string | null>(null);
  const consumed = useRef(false);
  const [form, setForm] = useState({
    code: initial?.code ?? '',
    name: initial?.name ?? '',
    phone: initial?.phone ?? '',
    email: initial?.email ?? '',
    tax_registration: initial?.tax_registration ?? '',
  });

  // Reserve the next branch code (BR-…) when creating; pre-fill it (editable),
  // and release it if the form is cancelled without saving.
  useEffect(() => {
    if (isEdit) return;
    let active = true;
    adminApi.reserveNumber('branch')
      .then((r: any) => {
        if (!active) { adminApi.releaseNumber(r.reservationId).catch(() => {}); return; }
        reservationId.current = r.reservationId;
        setForm((f) => (f.code ? f : { ...f, code: r.number }));
      })
      .catch(() => { /* fall back to manual entry */ });
    return () => {
      active = false;
      if (reservationId.current && !consumed.current) adminApi.releaseNumber(reservationId.current).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Administration</div>
        <h2 className="text-xl font-bold font-display text-ink-900">{isEdit ? 'Edit branch' : 'New branch'}</h2>
        {!isEdit && <p className="text-sm text-ink-500 mt-1">Add a new location. Warehouses and POS terminals attach to branches.</p>}
      </div>
      <div className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-[1fr_2fr] gap-3">
          <div>
            <label className="label">Code</label>
            <input className="input font-mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="auto" disabled={isEdit} />
          </div>
          <div>
            <label className="label">Name</label>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Main store" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Phone</label>
            <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 555 123 4567" />
          </div>
          <div>
            <label className="label">Email</label>
            <input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="store@example.com" />
          </div>
        </div>
        <div>
          <label className="label">Tax registration <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input font-mono" value={form.tax_registration} onChange={(e) => setForm({ ...form, tax_registration: e.target.value })} placeholder="VAT/GST number" />
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={loading || !form.code || !form.name}
          onClick={() => { consumed.current = true; onSubmit(isEdit
            ? { name: form.name, phone: form.phone || undefined, email: form.email || undefined, tax_registration: form.tax_registration || undefined }
            : { code: form.code, name: form.name, phone: form.phone || undefined, email: form.email || undefined, tax_registration: form.tax_registration || undefined }); }}>
          {loading ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create branch')}
        </button>
      </div>
    </Modal>
  );
}

function KpiTile({ label, value, accent = 'default' }: { label: string; value: number|string; accent?: 'default'|'sage'|'amber'|'danger' }) {
  const bar = { default:'bg-ink-200', sage:'bg-sage-500', amber:'bg-amber-500', danger:'bg-danger' }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">{typeof value === 'number' ? value.toLocaleString() : value}</div>
    </div>
  );
}

const ip = { width:16,height:16,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:2,strokeLinecap:'round' as const,strokeLinejoin:'round' as const };
function PlusIcon() { return <svg {...ip}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon() { return <svg {...ip} className="text-ink-400 flex-shrink-0"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
